import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useFormik} from 'formik'
import {toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'
import {AppUrls} from 'src/app/routing/AppUrls'
import {getOmsCustomerDetailsAsync, getOmsCustomersAsync} from 'src/redux/actions/omsCustomerActions'
import {
  getInventoryVariantsListAsync,
  getOmsPlatformFieldByOwnerAsync,
} from 'src/redux/actions/omsPageActions'
import {createOmsOrderAsync, saveOmsOrderCustomFieldsAsync} from 'src/redux/actions/omsOrderActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {buildCreateOrderPayload, extractCreatedOrderId} from './buildCreateOrderPayload'
import {buildOrderCustomFieldsPayload} from './buildOrderCustomFieldsPayload'
import {validateCreateOrderForm} from './validateCreateOrder'
import {validateRequiredCustomFields, validateCustomFieldFormats, processDuplicateLineItem} from './orderDataHelpers'
import {AddressState, CreateOrderFormSnapshot, OrderDiscountMode} from './types'
import OrderCustomFields from './OrderCustomFields'
import OmsAddressBlock from './OmsAddressBlock'
import {
  computeLineItemInventoryStatus,
  InventoryStatus,
  shouldEnforceInventoryOnSave,
  validateInventoryForSave,
} from '../oms-edit-order/inventoryValidationUtils'
import OmsPlatformFieldDataSelect from './OmsPlatformFieldDataSelect'
import {OmsTooltip, OmsInfoLabel, OmsInfoText} from './OmsTooltip'
import {
  extractPlatformFieldsList,
  findPlatformFieldByCode,
  isEmptyOrderFieldValue,
  OMS_ORDER_PLATFORM_FIELD_OWNER,
  pickDraftOrderStatusOption,
  pickFirstPlatformFieldOption,
  pickManualOrderChannelOption,
  useDefaultOmsPlatformFieldOption,
} from './platformFieldHelpers'
import './oms-create-order.css'
import './oms-order.css'
import PremiumBreadcrumb from 'src/app/components/navigation/PremiumBreadcrumb'
import {getCurrencySymbol} from 'src/app/pages/dashboard/utils/parseDashboardReportResponse'

// ─── Types ────────────────────────────────────────────────────────────────────

interface VariantOption {
  value: number            // platform_product_variant id
  label: string
  sku: string
  platform_product: number // platform_product id
  price: number
  availableQty: number
}

interface LineItem {
  id: string
  // per-row variant picker state
  variantOption: VariantOption | null
  variantSearch: string
  variantPage: number
  variantHasMore: boolean
  variantOptions: VariantOption[]
  variantLoading: boolean
  // order payload fields
  name: string
  sku: string
  platform_product: number | null
  platform_product_variant: number | null
  price: number | string
  quantity: number | string
  discount: number | string
  inventoryStatus?: InventoryStatus
}

// ─── Shared class constants ───────────────────────────────────────────────────
// Inputs / labels now use scoped CSS classes from oms-order.css
// Tailwind utility fallbacks kept only for one-off spacing inside sections

const inputCls = 'oms-input'
const inputSmCls = 'oms-input oms-input--sm'
const sectionCardCls = 'oms-card'
const sectionBodyCls = 'oms-card__body'

const getStockBadgeStyle = (availableQty: number) => {
  if (availableQty <= 0) return { bg: '#DC2626', border: '#DC2626', color: '#ffffff' }
  if (availableQty <= 10) return { bg: '#D97706', border: '#D97706', color: '#ffffff' }
  return { bg: '#16A34A', border: '#16A34A', color: '#ffffff' }
}

// ─── Factory for a blank line-item row ───────────────────────────────────────

const newLineItemRow = (): LineItem => ({
  id: `item-${Date.now()}-${Math.random()}`,
  variantOption: null,
  variantSearch: '',
  variantPage: 1,
  variantHasMore: true,
  variantOptions: [],
  variantLoading: false,
  name: '',
  sku: '',
  platform_product: null,
  platform_product_variant: null,
  price: 0,
  quantity: 1,
  discount: 0,
})

// ─── Customer helpers ─────────────────────────────────────────────────────────

const emptyAddress = (): AddressState => ({addr1: '', addr2: '', city: '', state: '', zip: '', country: ''})

const getCustomerName = (customer: any) => {
  const fullName = [customer?.fname || customer?.first_name, customer?.lname || customer?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return (
    customer?.display_name ||
    customer?.name ||
    fullName ||
    customer?.user_name ||
    customer?.email ||
    `Customer ${customer?.id || ''}`.trim()
  )
}

const getCustomerEmail = (customer: any) => customer?.email || customer?.user_name || ''
const getCustomerPhone = (customer: any) => customer?.phone || customer?.phone_number || ''

const getAddressObject = (customer: any, key: 'billing_address' | 'shipping_address' | 'address') => {
  const value = customer?.[key]
  if (value && typeof value === 'object') return value
  if (key !== 'address' && customer?.address && typeof customer.address === 'object') return customer.address
  return {}
}

const mapAddressToState = (address: any): AddressState => ({
  addr1: address?.address_1 || '',
  addr2: address?.address_2 || '',
  city: address?.city || '',
  state: address?.state || '',
  zip: address?.zip_code || address?.zip || '',
  country: address?.country || '',
})

// ─── Component ────────────────────────────────────────────────────────────────

const CreateOmsOrder: FC = () => {
  const dispatch = useAppDispatch()
  const {customerList, listLoading, detailLoading} = useAppSelector((state) => state.omsCustomer)
  const {saving: orderSaving} = useAppSelector((state) => state.omsOrder)
  const [saveCooldown, setSaveCooldown] = useState(false)
  const { connectedPlatformTypesLoading, omsStoreData } = useAppSelector((state) => state.fetchConnectedPlatform)
  const omsPlatformId = omsStoreData?.id ?? null
  const orderCurrency = omsStoreData?.currency_iso ?? 'USD'
  const currencySymbol = getCurrencySymbol(orderCurrency)
  const navigate = useNavigate()

  useEffect(() => {
    if (!connectedPlatformTypesLoading && !omsPlatformId) {
      toast.error('You must connect a Commercium OMS store to create orders.')
      navigate(AppUrls.allOrders())
    }
  }, [connectedPlatformTypesLoading, omsPlatformId, navigate])

  // ── Customer dropdown ────────────────────────────────────────────────────
  const dropdownRef = useRef<HTMLDivElement>(null)
  const lastCustomerSearch = useRef<string | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [customerSearch, setCustomerSearch] = useState('')
  const [triggerLabel, setTriggerLabel] = useState('Select Customer Profile...')
  const [showCustomerFields, setShowCustomerFields] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | string | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [billing, setBilling] = useState<AddressState>(emptyAddress())
  const [shipping, setShipping] = useState<AddressState>(emptyAddress())

  // ── New Customer Flow Validation ──────────────────────────────────────────
  const [sameAsBilling, setSameAsBilling] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false)

  const validateCustomerForm = useCallback((forceRequiredCheck: boolean) => {
    const newErrors: Record<string, string> = {}
    
    // Format errors always validate instantly
    if (phone.trim() && !/^\+?\d+$/.test(phone.trim())) {
      newErrors.phone = 'Phone number must be numeric (can start with +).'
    }

    const requiredAddrFields = ['addr1', 'city', 'state', 'zip', 'country'] as const
    const prefixes = ['billing', 'shipping'] as const
    const dataMap = { billing, shipping }

    prefixes.forEach(prefix => {
      const data = dataMap[prefix]
      if (data.zip?.trim() && !/^[A-Za-z0-9\s-]+$/.test(data.zip.trim())) {
        newErrors[`${prefix}.zip`] = 'Zip code must contain only letters, numbers, spaces, or hyphens.'
      }
    })

    // Required errors only validate if attempted save
    if (forceRequiredCheck) {
      if (!selectedCustomerId) {
        if (!firstName.trim()) newErrors.firstName = 'First name is required.'
        if (!lastName.trim()) newErrors.lastName = 'Last name is required.'
      }
      if (!email.trim()) newErrors.email = 'Email address is required.'
      if (!phone.trim()) newErrors.phone = 'Phone number is required.'
      
      prefixes.forEach(prefix => {
        const data = dataMap[prefix]
        requiredAddrFields.forEach(f => {
          if (!data[f]?.trim()) {
            newErrors[`${prefix}.${f}`] = 'This field is required.'
          }
        })
      })
    }
    
    return newErrors
  }, [firstName, lastName, email, phone, billing, shipping, selectedCustomerId])

  useEffect(() => {
    setFormErrors(validateCustomerForm(hasAttemptedSave))
  }, [validateCustomerForm, hasAttemptedSave])

  useEffect(() => {
    if (sameAsBilling) {
      setShipping(billing)
    }
  }, [billing, sameAsBilling])

  // ── Order config ─────────────────────────────────────────────────────────
  const [channelSource, setChannelSource] = useState('')
  const [orderStatus, setOrderStatus] = useState('')
  const [orderStatusLabel, setOrderStatusLabel] = useState<string | null>(null)
  const [orderStatusApiId, setOrderStatusApiId] = useState<string | null>(null)
  const [shippingMethod, setShippingMethod] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [shipmentDate, setShipmentDate] = useState('')
  const [orderConfigFields, setOrderConfigFields] = useState<any[]>([])
  const [orderConfigFieldsLoading, setOrderConfigFieldsLoading] = useState(false)
  const orderConfigFieldsPlatformRef = useRef<number | null>(null)

  // ── Warehouse (LOCATION platform field) ─────────────────────────────────
  const [warehouseId, setWarehouseId] = useState('')

  // ── Line items ────────────────────────────────────────────────────────────
  const variantTimers = useRef<Record<string, NodeJS.Timeout>>({})
  const [openVariantRow, setOpenVariantRow] = useState<string | null>(null)
  const [glowingRowId, setGlowingRowId] = useState<string | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{top: number; left: number; width: number} | null>(null)
  const activeVariantTriggerRef = useRef<HTMLElement | null>(null)
  const dropdownPanelRef = useRef<HTMLDivElement | null>(null)
  const [lineItems, setLineItems] = useState<LineItem[]>([])

  // ── Totals ────────────────────────────────────────────────────────────────
  const [shippingFee, setShippingFee] = useState<number | string>(0)
  const [taxCode, setTaxCode] = useState('')
  const [taxPercent, setTaxPercent] = useState<number | null>(null)
  const [taxPrice, setTaxPrice] = useState(0)
  const [amountPaid, setAmountPaid] = useState<number | string>(0)
  const [paymentRef, setPaymentRef] = useState('')
  const [orderDiscountMode, setOrderDiscountMode] = useState<OrderDiscountMode>('amount')
  const [orderDiscountValue, setOrderDiscountValue] = useState<number | string>('')

  // ── Custom fields (Formik-managed, owner=ORDER) ──────────────────────────
  const customFieldsFormik = useFormik<{custom_fields: Record<number, any>}>({
    initialValues: {custom_fields: {}},
    onSubmit: () => {},
  })
  const [orderFieldsMeta, setOrderFieldsMeta] = useState<any[]>([])

  // ── Notes ─────────────────────────────────────────────────────────────────
  const [internalComments, setInternalComments] = useState('')
  const [customerNotes, setCustomerNotes] = useState('')

  // ── Computed totals ───────────────────────────────────────────────────────
  const itemsSubtotal = lineItems.reduce((sum, item) => sum + Math.max(0, (Number(item.quantity) || 0) * (Number(item.price) || 0) - (Number(item.discount) || 0)), 0)
  const shippingNumeric = Number(shippingFee) || 0
  const orderDiscountNumeric = orderDiscountValue === '' ? 0 : Number(orderDiscountValue) || 0
  const orderDiscountBase = itemsSubtotal + shippingNumeric
  const orderDiscountAmount =
    orderDiscountNumeric <= 0
      ? 0
      : orderDiscountMode === 'percent'
        ? orderDiscountBase * (orderDiscountNumeric / 100)
        : orderDiscountNumeric
  const calculatedTax = taxPrice
  const grandTotal = Math.max(0, orderDiscountBase - orderDiscountAmount + calculatedTax)

  useEffect(() => {
    if (taxPercent != null) {
      setTaxPrice((itemsSubtotal + (Number(shippingFee) || 0)) * (taxPercent / 100))
    } else {
      setTaxPrice(0)
    }
  }, [itemsSubtotal, shippingFee, taxPercent])

  useEffect(() => {
    if ((Number(amountPaid) || 0) > grandTotal) {
      setAmountPaid(grandTotal)
    }
  }, [grandTotal, amountPaid])

  const balanceDue = Math.max(0, grandTotal - (Number(amountPaid) || 0))
  const enforceLineItemInventory = shouldEnforceInventoryOnSave(orderStatusApiId)

  useEffect(() => {
    if (!omsPlatformId || orderConfigFieldsPlatformRef.current === omsPlatformId) return

    let isMounted = true
    const loadOrderConfigFields = async () => {
      orderConfigFieldsPlatformRef.current = omsPlatformId
      setOrderConfigFieldsLoading(true)
      try {
        const [orderResponse, shipmentResponse] = await Promise.all([
          dispatch(
            getOmsPlatformFieldByOwnerAsync({
              platformId: omsPlatformId,
              owner: OMS_ORDER_PLATFORM_FIELD_OWNER,
            })
          ).unwrap(),
          dispatch(
            getOmsPlatformFieldByOwnerAsync({
              platformId: omsPlatformId,
              owner: 'SHIPMENT',
            })
          ).unwrap()
        ])

        if (isMounted) {
          setOrderConfigFields([
            ...extractPlatformFieldsList(orderResponse),
            ...extractPlatformFieldsList(shipmentResponse)
          ])
        }
      } catch (error) {
        console.error('Failed to load ORDER/SHIPMENT platform fields', error)
      } finally {
        if (isMounted) setOrderConfigFieldsLoading(false)
      }
    }

    loadOrderConfigFields()

    return () => {
      isMounted = false
    }
  }, [dispatch, omsPlatformId])

  const getOrderConfigFieldId = (fieldCode: string) => {
    const field = findPlatformFieldByCode(orderConfigFields, fieldCode)
    return field?.id ?? null
  }

  // ── Outside-click: customer dropdown ─────────────────────────────────────
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  // ── Outside-click: variant row dropdowns ─────────────────────────────────
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Element
      if (!target.closest('[data-variant-dropdown]')) {
        setOpenVariantRow(null)
        setDropdownCoords(null)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  const updateVariantDropdownPosition = useCallback(() => {
    if (!activeVariantTriggerRef.current) return
    const rect = activeVariantTriggerRef.current.getBoundingClientRect()
    const top = rect.bottom
    const left = rect.left
    const width = Math.max(rect.width, 320)
    // Direct DOM update for instant, jitter-free tracking during scroll
    if (dropdownPanelRef.current) {
      dropdownPanelRef.current.style.top = `${top}px`
      dropdownPanelRef.current.style.left = `${left}px`
      dropdownPanelRef.current.style.width = `${width}px`
    }
    // Also update state so initial render is correct
    setDropdownCoords({ top, left, width })
  }, [])

  useEffect(() => {
    if (openVariantRow) {
      const handleScroll = (e: Event) => {
        if (dropdownPanelRef.current?.contains(e.target as Node)) return
        setOpenVariantRow(null)
      }
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [openVariantRow])

  useEffect(() => {
    if (!openVariantRow) {
      setDropdownCoords(null)
      activeVariantTriggerRef.current = null
    }
  }, [openVariantRow])

  // ── Customer search debounce ──────────────────────────────────────────────
  useEffect(() => {
    if (!dropdownOpen) return
    if (lastCustomerSearch.current === customerSearch) return

    const timer = window.setTimeout(() => {
      dispatch(getOmsCustomersAsync({search: customerSearch.trim() || undefined, page: 1, limit: 10, include: 'address', type: 'customer', sort_by: 'asc', order_by: 'id'}))
      lastCustomerSearch.current = customerSearch
    }, 300)
    return () => window.clearTimeout(timer)
  }, [customerSearch, dispatch, dropdownOpen])

  const handleWarehouseChange = (val: string) => {
    setWarehouseId(val)
    if (!val) return
    setLineItems((prev) =>
      prev.map((item) => ({
        ...item,
        variantOption: null,
        name: '',
        sku: '',
        platform_product: null,
        platform_product_variant: null,
        variantOptions: [],
        variantSearch: '',
        variantPage: 1,
        variantHasMore: true,
      }))
    )
  }

  useDefaultOmsPlatformFieldOption({
    fieldId: getOrderConfigFieldId('ORDER_STATUS'),
    enabled: !orderConfigFieldsLoading && isEmptyOrderFieldValue(orderStatus),
    pick: pickDraftOrderStatusOption,
    onApply: (option) => {
      setOrderStatus(option.value)
      setOrderStatusLabel(option.label)
      setOrderStatusApiId(option.raw?.api_id ?? null)
    },
  })

  useDefaultOmsPlatformFieldOption({
    fieldId: getOrderConfigFieldId('CHANNEL'),
    enabled: !orderConfigFieldsLoading && isEmptyOrderFieldValue(channelSource),
    pick: pickManualOrderChannelOption,
    onApply: (option) => setChannelSource(option.value),
  })

  useDefaultOmsPlatformFieldOption({
    fieldId: getOrderConfigFieldId('LOCATION'),
    enabled: !orderConfigFieldsLoading && isEmptyOrderFieldValue(warehouseId),
    pick: pickFirstPlatformFieldOption,
    onApply: (option) => setWarehouseId(option.value),
  })

  // ── Variant fetch (per row) ───────────────────────────────────────────────

  const fetchVariantOptions = useCallback(
    async (itemId: string, page: number, search: string) => {
      if (!warehouseId) return

      setLineItems((prev) =>
        prev.map((item) => (item.id === itemId ? {...item, variantLoading: true} : item))
      )

      try {
        const res: any = await dispatch(
          getInventoryVariantsListAsync({
            page,
            limit: 10,
            search: search.trim() || undefined,
            sort_by: 'asc',
            order_by: 'id',
            source_warehouse_id: warehouseId,
            destination_warehouse_id: warehouseId,
          })
        )

        const data: any[] = res?.payload?.results?.data || []
        const hasMore = !!res?.payload?.next

        const newOptions: VariantOption[] = data.map((it: any) => {
          let parsedPrice = 0;
          if (it.price != null) {
            if (typeof it.price === 'object' && 'value' in it.price) {
              parsedPrice = Number(it.price.value) || 0;
            } else {
              parsedPrice = Number(it.price) || 0;
            }
          }
          return {
            value: it.id,
            label: it.name || it.sku || `Variant ${it.id}`,
            sku: it.sku || '',
            platform_product: it.platform_product_id ?? 0,
            price: parsedPrice,
            availableQty: it.source_total_quantity ?? it.total_quantity ?? 0,
          }
        })

        setLineItems((prev) =>
          prev.map((item) => {
            if (item.id !== itemId) return item
            return {
              ...item,
              variantOptions: page === 1 ? newOptions : [...item.variantOptions, ...newOptions],
              variantPage: page,
              variantHasMore: hasMore,
              variantLoading: false,
            }
          })
        )
      } catch {
        setLineItems((prev) =>
          prev.map((item) => (item.id === itemId ? {...item, variantLoading: false} : item))
        )
      }
    },
    [dispatch, warehouseId]
  )

  const openVariantDropdown = (itemId: string, el: HTMLElement) => {
    activeVariantTriggerRef.current = el
    updateVariantDropdownPosition()
    setOpenVariantRow(itemId)
    const item = lineItems.find((i) => i.id === itemId)
    if (item && item.variantOptions.length === 0 && warehouseId) {
      fetchVariantOptions(itemId, 1, item.variantSearch)
    }
  }

  const handleVariantSearch = (itemId: string, search: string) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === itemId ? {...item, variantSearch: search, variantPage: 1} : item))
    )
    if (variantTimers.current[itemId]) clearTimeout(variantTimers.current[itemId])
    variantTimers.current[itemId] = setTimeout(() => fetchVariantOptions(itemId, 1, search), 300)
  }

  const handleVariantScroll = (e: React.UIEvent<HTMLDivElement>, item: LineItem) => {
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50 && item.variantHasMore && !item.variantLoading) {
      fetchVariantOptions(item.id, item.variantPage + 1, item.variantSearch)
    }
  }

  const selectVariant = (itemId: string, opt: VariantOption) => {
    if (processDuplicateLineItem(lineItems, opt, itemId, setGlowingRowId, setOpenVariantRow, setLineItems)) return

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item
        return {
          ...item,
          variantOption: opt,
          name: opt.label,
          sku: opt.sku,
          platform_product: opt.platform_product,
          platform_product_variant: opt.value,
          price: opt.price != null ? String(opt.price) : item.price,
          variantSearch: '',
          variantOptions: [],
          variantPage: 1,
          variantHasMore: true,
          inventoryStatus: computeLineItemInventoryStatus(opt.availableQty, item.quantity),
        }
      })
    )
    setOpenVariantRow(null)
  }

  // ── Customer handlers ─────────────────────────────────────────────────────

  const hydrateCustomer = (customer: any) => {
    setSelectedCustomerId(customer?.id ?? null)
    setTriggerLabel(getCustomerName(customer))
    setShowCustomerFields(true)
    setEmail(getCustomerEmail(customer))
    setPhone(getCustomerPhone(customer))
    setBilling(mapAddressToState(getAddressObject(customer, 'billing_address')))
    setShipping(mapAddressToState(getAddressObject(customer, 'shipping_address')))
  }

  const selectProfile = async (customer: any) => {
    hydrateCustomer(customer)
    setDropdownOpen(false)
    setCustomerSearch('')
    const customerId = customer?.id
    if (!customerId) return
    try {
      const response: any = await dispatch(getOmsCustomerDetailsAsync({id: customerId, include: 'address,additional_data'})).unwrap()
      hydrateCustomer(response?.data || response)
    } catch (error) {
      console.error('Unable to load customer details', error)
    }
  }

  const createNewCustomer = () => {
    setTriggerLabel('New Customer Profile Setup')
    setDropdownOpen(false)
    setCustomerSearch('')
    setSelectedCustomerId(null)
    setShowCustomerFields(true)
    setFirstName('')
    setLastName('')
    setDisplayName('')
    setEmail('')
    setPhone('')
    setBilling(emptyAddress())
    setShipping(emptyAddress())
  }

  // ── Line item handlers ────────────────────────────────────────────────────

  const addLineItem = () => setLineItems((prev) => [...prev, newLineItemRow()])

  const updateLineItem = (
    id: string,
    field: keyof Pick<LineItem, 'quantity' | 'price' | 'discount'>,
    value: number | string
  ) => setLineItems((prev) => prev.map((item) => {
    if (item.id !== id) return item
    const updated = { ...item, [field]: value }
    if (field === 'quantity' && updated.variantOption?.availableQty != null) {
      updated.inventoryStatus = computeLineItemInventoryStatus(
        updated.variantOption.availableQty,
        value
      )
    }
    return updated
  }))

  const removeLineItem = (id: string) => setLineItems((prev) => prev.filter((item) => item.id !== id))

  const getLineSubtotal = (item: LineItem) => Math.max(0, (Number(item.quantity) || 0) * (Number(item.price) || 0) - (Number(item.discount) || 0))

  const buildFormSnapshot = (): CreateOrderFormSnapshot => ({
    customerId: selectedCustomerId,
    firstName,
    lastName,
    displayName,
    email,
    phone,
    billing,
    shipping,
    warehouseId: warehouseId || null,
    lineItems: lineItems.map((item) => ({
      platform_product: item.platform_product,
      platform_product_variant: item.platform_product_variant,
      name: item.name,
      sku: item.sku,
      quantity: Number(item.quantity) || 0,
      price: Number(item.price) || 0,
      discount: Number(item.discount) || 0,
    })),
    calculatedTax,
    grandTotal,
    shippingFee: Number(shippingFee) || 0,
    shippingMethod,
    channelSource,
    paymentMethod,
    orderStatus,
    orderStatusLabel,
    orderStatusApiId,
    amountPaid: Number(amountPaid) || 0,
    paymentRef,
    shipmentDate,
    customerNotes,
    orderNotes: internalComments,
    taxCode,
    taxPercent,
    orderCurrency,
    dutyPrice: 0,
    orderDiscountMode,
    orderDiscountValue: orderDiscountValue === '' ? '' : Number(orderDiscountValue) || 0,
    priceListId: '',
  })

  const handleCancel = () => {
    navigate(AppUrls.allOrders())
  }

  const handleSaveOrder = async () => {
    if (saveCooldown) return
    setSaveCooldown(true)
    setTimeout(() => setSaveCooldown(false), 5000)
    const inventoryError = validateInventoryForSave(lineItems, orderStatusApiId, orderStatusLabel)
    if (inventoryError) {
      toast.error(inventoryError)
      return
    }

    setHasAttemptedSave(true)
    const newErrors = validateCustomerForm(true)
    setFormErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fix the highlighted customer information errors.')
      setSaveCooldown(false)
      return
    }

    const snapshot = buildFormSnapshot()
    const validation = validateCreateOrderForm(snapshot)

    if (!validation.valid) {
      toast.error(validation.message)
      return
    }

    const cfErrors = validateRequiredCustomFields(customFieldsFormik.values.custom_fields, orderFieldsMeta)
    if (cfErrors.length > 0) {
      toast.error(cfErrors[0])
      return
    }

    const formatErrors = validateCustomFieldFormats(customFieldsFormik.values.custom_fields, orderFieldsMeta)
    if (formatErrors.length > 0) {
      toast.error(formatErrors[0])
      return
    }

    try {
      const orderResponse = await dispatch(
        createOmsOrderAsync({data: buildCreateOrderPayload(snapshot), is_confirmed: false})
      ).unwrap()

      const orderId = extractCreatedOrderId(orderResponse)
      if (!orderId) {
        toast.error('Order was created but no order ID was returned.')
        return
      }

      const customFieldsPayload = buildOrderCustomFieldsPayload(
        customFieldsFormik.values.custom_fields,
        orderFieldsMeta
      )

      if (customFieldsPayload.length) {
        try {
          await dispatch(saveOmsOrderCustomFieldsAsync({orderId, data: customFieldsPayload})).unwrap()
        } catch (cfError: any) {
          console.error('Order custom fields save failed', cfError)
          toast.success(orderResponse?.message || 'Sales order created successfully.')
          toast.warning(
            cfError?.data?.message ||
              'Custom fields could not be saved. You can update them later.'
          )
          navigate(AppUrls.allOrders())
          return
        }
      }

      toast.success(orderResponse?.message || 'Sales order created successfully.')
      navigate(AppUrls.allOrders())
    } catch (error: any) {
      console.error('Create order failed', error)
      toast.error(error?.data?.message || error?.message || 'Failed to create order.')
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div id='oms-create-order-root' className='omsOrderScope'>
      <div className='omsOrderScope__scroll'>

      {/* ══ STICKY HEADER ══ */}
      <div className='omsOrderScope__header'>
        <div className='flex items-center justify-between'>
          <div className='flex flex-col'>
            <PremiumBreadcrumb
              items={[
                { label: 'Sales Order', to: AppUrls.allOrders() },
                { label: 'Create New Order' },
              ]}
            />
            <span style={{fontSize: 16, fontWeight: 700, color: 'var(--text)'}}>Create New Order</span>
          </div>
          <div style={{display: 'flex', gap: 10}}>
            <OmsTooltip tip='Discard this draft without saving'>
              <button type='button' className='oms-btn oms-btn--ghost' onClick={handleCancel}>
                Cancel
              </button>
            </OmsTooltip>
            <OmsTooltip tip='Save and create this order'>
              <button
                type='button'
                onClick={handleSaveOrder}
                disabled={orderSaving || saveCooldown}
                className='oms-btn oms-btn--primary'
              >
                {orderSaving ? 'Saving…' : 'Save Order'}
              </button>
            </OmsTooltip>
          </div>
        </div>
      </div>

      <div className='omsOrderScope__content'>

        {/* ══ Section 1 · Customer ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Customer Selection &amp; Information</h2></div>
          <div className={sectionBodyCls}>

          <div className='mb-[25px] max-w-[50%]'>
            <OmsInfoLabel text='Select or Create Customer' required tip='Search an existing customer profile or create a new one to attach to this order' />
            <div className='relative w-full' ref={dropdownRef}>
              <div
                className='flex justify-between items-center px-3 py-2 border border-[#007bff] rounded-[4px] text-[13px] cursor-pointer bg-white select-none'
                onClick={() => setDropdownOpen((o) => !o)}
              >
                <span>{triggerLabel}</span>
                <span>▼</span>
              </div>

              {dropdownOpen && (
                <div className='absolute top-full left-0 right-0 bg-white border border-[#dee2e6] rounded-[4px] mt-1 shadow-[0_4px_12px_rgba(0,0,0,0.1)] z-[1000]'>
                  <div className='p-2 border-b border-[#eeeeee]'>
                    <input
                      autoFocus
                      className={inputCls}
                      placeholder='Search profile database...'
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className='max-h-[180px] overflow-y-auto'>
                    {listLoading && (
                      <div className='px-3 py-[10px] text-[13px] text-[#6c757d]'>Searching customers...</div>
                    )}
                    {!listLoading && customerList.map((customer: any) => (
                      <div
                        key={customer?.id || customer?.api_id || getCustomerName(customer)}
                        className='px-3 py-[10px] text-[13px] cursor-pointer border-b border-[#f9f9f9] hover:bg-[#f8f9fa]'
                        onClick={() => selectProfile(customer)}
                      >
                        <div className='font-semibold'>{getCustomerName(customer)}</div>
                        <div className='text-[11px] text-[#6c757d] mt-[2px]'>
                          {getCustomerEmail(customer) || getCustomerPhone(customer) || `ID: ${customer?.id}`}
                        </div>
                      </div>
                    ))}
                    {!listLoading && customerList.length === 0 && (
                      <div className='px-3 py-[10px] text-[13px] text-[#6c757d]'>No results found.</div>
                    )}
                  </div>
                  <div
                    className='p-[10px] border-t border-[#eeeeee] text-[#007bff] text-[12px] font-semibold cursor-pointer hover:bg-[#f8f9fa]'
                    onClick={createNewCustomer}
                  >
                    ✚ Create New Customer Account
                  </div>
                </div>
              )}
            </div>
          </div>

          {showCustomerFields && (
            <div>
              {detailLoading && (
                <div className='text-[12px] text-[#007bff] bg-[#e6f2ff] px-3 py-2 rounded-[4px] mb-[18px] inline-flex items-center gap-[6px] font-medium'>
                  Loading selected customer details...
                </div>
              )}
              {selectedCustomerId && (
                <div className='text-[12px] text-[#6c757d] mb-[18px]'>
                  Selected Customer ID: {selectedCustomerId}
                </div>
              )}

              {!selectedCustomerId && (
                <div className='grid grid-cols-3 gap-[20px] mb-[20px]'>
                  <div>
                    <OmsInfoLabel text='First Name' required tip='Customer first name' />
                    <input className={`${inputCls} ${formErrors.firstName ? 'border-danger' : ''}`} placeholder='First Name' value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    {formErrors.firstName && <div className='text-danger text-[11px] mt-1'>{formErrors.firstName}</div>}
                  </div>
                  <div>
                    <OmsInfoLabel text='Last Name' required tip='Customer last name' />
                    <input className={`${inputCls} ${formErrors.lastName ? 'border-danger' : ''}`} placeholder='Last Name' value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    {formErrors.lastName && <div className='text-danger text-[11px] mt-1'>{formErrors.lastName}</div>}
                  </div>
                  <div>
                    <OmsInfoLabel text='Display Name' tip='Customer display name' />
                    <input className={inputCls} placeholder='Display Name' value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                  </div>
                </div>
              )}

              <div className='grid grid-cols-2 gap-[20px] mb-[25px]'>
                <div>
                  <OmsInfoLabel text='Email Address' required tip='Contact email used for order notifications and receipts' />
                  <input type='email' className={`${inputCls} ${formErrors.email ? 'border-danger' : ''}`} placeholder='customer@domain.com' value={email} onChange={(e) => setEmail(e.target.value)} />
                  {formErrors.email && <div className='text-danger text-[11px] mt-1'>{formErrors.email}</div>}
                </div>
                <div>
                  <OmsInfoLabel text='Phone Number' required tip='Contact phone number for delivery and support' />
                  <input type='tel' className={`${inputCls} ${formErrors.phone ? 'border-danger' : ''}`} placeholder='+1 (555) 000-0000' value={phone} onChange={(e) => setPhone(e.target.value)} />
                  {formErrors.phone && <div className='text-danger text-[11px] mt-1'>{formErrors.phone}</div>}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-[20px]'>
                <OmsAddressBlock
                  title='Billing Address'
                  data={billing}
                  onChange={(field, val) => setBilling((b) => ({...b, [field]: val}))}
                  onAddressChange={(updates) => setBilling((b) => ({...b, ...updates}))}
                  prefix='billing'
                  errors={formErrors}
                  required
                  inputCls={inputCls}
                />
                <OmsAddressBlock
                  title='Shipping Address'
                  data={shipping}
                  onChange={(field, val) => setShipping((s) => ({...s, [field]: val}))}
                  onAddressChange={(updates) => setShipping((s) => ({...s, ...updates}))}
                  prefix='shipping'
                  errors={formErrors}
                  disabled={sameAsBilling}
                  showToggle
                  toggleChecked={sameAsBilling}
                  onToggle={(checked) => setSameAsBilling(checked)}
                  required
                  inputCls={inputCls}
                />
              </div>
            </div>
          )}
          </div>
        </div>

        {/* ══ Section 2 · Order Configurations ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Order Configurations &amp; Statuses</h2></div>
          <div className={sectionBodyCls}>

          <div className='oms-info-banner'>
            ⚙️ Order Number will be generated automatically upon save
          </div>

          {/* Row 1 */}
          <div className='grid grid-cols-3 gap-[20px] mb-[20px]'>
            <div>
              <OmsInfoLabel text='Order Source' required tip='The sales channel this order originates from' />
              <OmsPlatformFieldDataSelect
                fieldId={getOrderConfigFieldId('CHANNEL')}
                value={channelSource}
                onChange={setChannelSource}
                placeholder='Select Order Source...'
                loading={orderConfigFieldsLoading}
              />
            </div>
            <div>
              <OmsInfoLabel text='Order Status' required tip='Current lifecycle stage of the order (e.g. Pending, Processing, Shipped)' />
              <OmsPlatformFieldDataSelect
                fieldId={getOrderConfigFieldId('ORDER_STATUS')}
                value={orderStatus}
                onChange={(val, label, raw) => {
                  setOrderStatus(val)
                  setOrderStatusLabel(label)
                  setOrderStatusApiId(raw?.api_id ?? null)
                }}
                placeholder='Select Order Status...'
                loading={orderConfigFieldsLoading}
              />
            </div>
            <div>
              <OmsInfoLabel text='Fulfillment State' tip='New orders start as UnFulfilled. This updates automatically as packages and shipments are created.' />
              <div className='inline-block px-3 py-[6px] bg-[#fff3cd] text-[#856404] rounded-[4px] font-semibold text-[13px]'>
                UnFulfilled
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className='grid grid-cols-4 gap-[20px] mb-[20px]'>

            {/* ── Warehouse (LOCATION platform field) ── */}
            <div>
              <OmsInfoLabel text='Warehouse' required tip='The warehouse that will fulfill this order. Determines available stock for line items.' />
              <OmsPlatformFieldDataSelect
                fieldId={getOrderConfigFieldId('LOCATION')}
                value={warehouseId}
                onChange={handleWarehouseChange}
                placeholder='Select Warehouse...'
                loading={orderConfigFieldsLoading}
              />
            </div>

            <div>
              <OmsInfoLabel text='Shipping Carrier & Method' tip='Carrier and service level used to ship this order' />
              <OmsPlatformFieldDataSelect
                fieldId={getOrderConfigFieldId('SHIPPINGPROVIDER')}
                value={shippingMethod}
                onChange={setShippingMethod}
                placeholder='Select Shipping Method...'
                loading={orderConfigFieldsLoading}
              />
            </div>
            <div>
              <OmsInfoLabel text='Payment Gateway / Method' tip='Payment method or gateway used to collect payment' />
              <OmsPlatformFieldDataSelect
                fieldId={getOrderConfigFieldId('PAYMENT_METHOD')}
                value={paymentMethod}
                onChange={setPaymentMethod}
                placeholder='Select Payment Method...'
                loading={orderConfigFieldsLoading}
              />
            </div>
            <div>
              <OmsInfoLabel text='Expected Shipment Date' tip='Target date this order is expected to ship' />
              <input type='date' className={`${inputCls} ${!shipmentDate ? 'is-empty-date' : ''}`} value={shipmentDate} onChange={(e) => setShipmentDate(e.target.value)} />
            </div>
          </div>
          </div>
        </div>

        {/* ══ Section 3 · Line Items ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Order Items &amp; Inventory Demands</h2></div>
          <div className={sectionBodyCls}>


          {/* Items table */}
          <div className='w-full overflow-x-auto pb-[10px]'>
            <table className='w-full border-collapse mt-[15px] min-w-[800px]'>
            <thead>
              <tr>
                <th className='text-left bg-[#f8f9fa] p-3 text-[11px] uppercase text-[#6c757d] border-b-2 border-[#dee2e6]' style={{width: '42%'}}>
                  <OmsInfoText className='oms-th-hint' text='Product / Variant / SKU' tip='Search and select a product variant from the chosen warehouse' />
                </th>
                <th className='text-left bg-[#f8f9fa] p-3 text-[11px] uppercase text-[#6c757d] border-b-2 border-[#dee2e6]' style={{width: '10%'}}>
                  <OmsInfoText className='oms-th-hint' text='Quantity' tip='Number of units ordered for this line item' />
                </th>
                <th className='text-left bg-[#f8f9fa] p-3 text-[11px] uppercase text-[#6c757d] border-b-2 border-[#dee2e6]' style={{width: '14%'}}>
                  <OmsInfoText className='oms-th-hint' required text='Unit Price' tip='Price per unit before discount and tax' />
                </th>
                <th className='text-left bg-[#f8f9fa] p-3 text-[11px] uppercase text-[#6c757d] border-b-2 border-[#dee2e6]' style={{width: '14%'}}>
                  <OmsInfoText className='oms-th-hint' text={`Line Discount (${currencySymbol})`} tip='Flat discount amount applied to this line item' />
                </th>
                <th className='text-left bg-[#f8f9fa] p-3 text-[11px] uppercase text-[#6c757d] border-b-2 border-[#dee2e6]' style={{width: '10%'}}>
                  <OmsInfoText className='oms-th-hint' text='Subtotal' tip='Calculated line total: (quantity x unit price) - discount' />
                </th>
                <th className='text-center bg-[#f8f9fa] p-3 text-[11px] uppercase text-[#6c757d] border-b-2 border-[#dee2e6]' style={{width: '10%'}}>
                  <OmsInfoText className='oms-th-hint' text='Actions' tip='Remove a line item from the order' />
                </th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className='text-center text-[#6c757d] p-[30px] border-b border-[#dee2e6]'>
                    {warehouseId
                      ? 'No line items yet. Use "+ Add Line Item" below to start adding products.'
                      : 'Select a warehouse first, then add line items.'}
                  </td>
                </tr>
              ) : (
                lineItems.map((item) => (
                  <tr 
                    key={item.id}
                    style={item.id === glowingRowId ? { backgroundColor: '#fef9c3', transition: 'background-color 0.5s ease-in-out' } : { transition: 'background-color 0.5s ease-in-out' }}
                  >

                    {/* ── Variant picker cell ── */}
                    <td className='p-3 border-b border-[#dee2e6] align-middle'>
                      <div style={{ width: '100%', maxWidth: '380px' }}>
                      <div data-variant-dropdown='true' style={{position: 'relative'}}>

                        {/* Trigger: shows search input when open, selected label when closed */}
                        {openVariantRow === item.id ? (
                          <div
                            className='flex items-center px-[14px] py-[10px] bg-white'
                            style={{ 
                              border: '1px solid #3b82f6', 
                              borderRadius: '8px',
                              minHeight: '42px',
                              boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.12)',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#3b82f6', marginRight: '10px', marginLeft: '10px', flexShrink: 0 }}>
                              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                            </svg>
                            <input
                              autoFocus
                              className='flex-1 !outline-none !border-none !shadow-none bg-transparent text-[13px] text-[#333] placeholder-[#adb5bd] p-0 focus:!ring-0 focus:!outline-none focus:!border-transparent focus:!shadow-none ring-0'
                              style={{ boxShadow: 'none', outline: 'none', border: 'none', WebkitAppearance: 'none' }}
                              placeholder='Type to search variants...'
                              value={item.variantSearch}
                              onChange={(e) => handleVariantSearch(item.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {item.variantSearch && (
                              <button
                                className='ml-2 flex items-center justify-center text-[#adb5bd] hover:text-[#495057] hover:bg-[#f1f3f5]'
                                onClick={(e) => { e.stopPropagation(); handleVariantSearch(item.id, '') }}
                                style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', transition: 'all 0.15s ease' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ) : (
                          <div
                            className='flex justify-between items-center px-[14px] py-[10px] bg-white select-none'
                            style={{
                              border: '1px solid #d1d5db', 
                              borderRadius: '8px',
                              minHeight: '42px',
                              cursor: warehouseId ? 'pointer' : 'not-allowed', 
                              opacity: warehouseId ? 1 : 0.6,
                              transition: 'border-color 0.2s ease',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            onClick={(e) => warehouseId && openVariantDropdown(item.id, e.currentTarget)}
                            onMouseEnter={(e) => { if (warehouseId) e.currentTarget.style.borderColor = '#9ca3af' }}
                            onMouseLeave={(e) => { if (warehouseId) e.currentTarget.style.borderColor = '#d1d5db' }}
                          >
                            <div className='flex items-center overflow-hidden' style={{ flex: 1 }}>
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#adb5bd', marginRight: '10px', marginLeft: '10px', flexShrink: 0 }}>
                                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                              </svg>
                              <span style={{color: item.variantOption ? '#333333' : '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: '13px', lineHeight: '20px'}}>
                                {item.variantOption
                                  ? item.variantOption.label
                                  : warehouseId
                                  ? 'Search and select a variant...'
                                  : 'Select warehouse first'}
                              </span>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#adb5bd', marginLeft: '8px', flexShrink: 0 }}>
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}

                        {/* Selected variant meta */}
                        {item.variantOption && (
                          <div className='flex flex-wrap items-center gap-[6px] mt-[4px]'>
                            {item.sku && (
                              <span style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', color: '#495057', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                SKU: {item.sku}
                              </span>
                            )}
                            {item.variantOption.availableQty != null && (() => {
                              const style = getStockBadgeStyle(item.variantOption.availableQty)
                              return (
                                <span style={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                  Available: {item.variantOption.availableQty}
                                </span>
                              )
                            })()}
                          </div>
                        )}
                        {/* INVENTORY STATUS BANNERS 
                        {item.inventoryStatus === 'NOT_FOUND' && (
                          <div style={{ backgroundColor: '#fff5f5', color: '#c92a2a', border: '1px solid #ffe3e3', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                            <span style={{ fontSize: '14px', lineHeight: '1' }}>⚠️</span>
                            <div>
                              <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>SKU Not Found</strong>
                              Remove this item or change warehouse.
                            </div>
                          </div>
                        )}
                        {item.inventoryStatus === 'OUT_OF_STOCK' && (
                          <div style={{ backgroundColor: '#fff5f5', color: '#c92a2a', border: '1px solid #ffe3e3', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                            <span style={{ fontSize: '14px', lineHeight: '1' }}>🚫</span>
                            <div>
                              <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>Out of Stock (0 available)</strong>
                              Remove this item or change warehouse.
                            </div>
                          </div>
                        )}
                        {item.inventoryStatus === 'INSUFFICIENT_STOCK' && (
                          <div style={{ backgroundColor: '#fffbe6', color: '#d48806', border: '1px solid #ffe58f', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                            <span style={{ fontSize: '14px', lineHeight: '1' }}>⚡</span>
                            <div>
                              <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>Insufficient Stock (Only {item.variantOption?.availableQty} available)</strong>
                              Reduce quantity or change warehouse.
                            </div>
                          </div>
                        )}
                        */}

                        {/* Dropdown panel */}
                        {openVariantRow === item.id && dropdownCoords && createPortal(
                          <div
                            ref={dropdownPanelRef}
                            data-variant-dropdown='true'
                            className='z-[99999]'
                            style={{
                              position: 'fixed',
                              top: `${dropdownCoords.top}px`,
                              left: `${dropdownCoords.left}px`,
                              width: `${dropdownCoords.width}px`,
                              background: '#ffffff',
                              border: '1px solid #dee2e6',
                              borderRadius: '8px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                              overflow: 'hidden',
                              willChange: 'top, left',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className='overflow-y-auto'
                              style={{ maxHeight: '260px' }}
                              onScroll={(e) => handleVariantScroll(e, item)}
                            >
                              {item.variantLoading && item.variantOptions.length === 0 && (
                                <div style={{ padding: '20px 16px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                                  <span style={{ display: 'block', marginBottom: '4px', fontSize: '18px' }}>⏳</span>
                                  Loading variants...
                                </div>
                              )}
                              {item.variantOptions.map((opt, idx) => {
                                const isSelected = item.variantOption?.value === opt.value
                                const lowStock = opt.availableQty != null && opt.availableQty <= 0
                                const stockStyle = opt.availableQty != null ? getStockBadgeStyle(opt.availableQty) : null
                                return (
                                  <div
                                    key={opt.value}
                                    onClick={() => selectVariant(item.id, opt)}
                                    style={{
                                      padding: '10px 14px',
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                                      borderBottom: idx < item.variantOptions.length - 1 ? '1px solid #f1f3f5' : 'none',
                                      opacity: lowStock && enforceLineItemInventory ? 0.55 : 1,
                                      transition: 'background-color 0.12s ease',
                                    }}
                                    onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f8f9fa' }}
                                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected ? '#eff6ff' : 'transparent' }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: isSelected ? '#1d4ed8' : '#212529', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {isSelected && <span style={{ marginRight: '5px', color: '#1d4ed8' }}>✓</span>}
                                          {opt.label}
                                        </div>
                                        {opt.sku && (
                                          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px', fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                                            SKU: {opt.sku}
                                          </div>
                                        )}
                                      </div>
                                      {stockStyle && (
                                        <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', backgroundColor: stockStyle.bg, border: `1px solid ${stockStyle.border}`, color: stockStyle.color, whiteSpace: 'nowrap', alignSelf: 'center' }}>
                                          {opt.availableQty} Available
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                              {!item.variantLoading && item.variantOptions.length === 0 && (
                                <div style={{ padding: '24px 16px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                                  <span style={{ display: 'block', marginBottom: '6px', fontSize: '22px' }}>🔍</span>
                                  No variants found
                                  {item.variantSearch && <div style={{ marginTop: '4px', fontSize: '12px', color: '#adb5bd' }}>Try a different search term</div>}
                                </div>
                              )}
                              {item.variantLoading && item.variantOptions.length > 0 && (
                                <div style={{ padding: '10px 16px', textAlign: 'center', color: '#6c757d', fontSize: '12px', borderTop: '1px solid #f1f3f5' }}>
                                  Loading more...
                                </div>
                              )}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                      </div>
                    </td>

                    {/* Quantity */}
                    <td className='p-3 border-b border-[#dee2e6] align-middle'>
                      <input
                        type='number'
                        step='any'
                        min={1}
                        max={
                          enforceLineItemInventory
                            ? item.variantOption?.availableQty ?? undefined
                            : undefined
                        }
                        value={item.quantity}
                        onChange={(e) => {
                          let val = e.target.value.replace(/^0+(?=\d)/, '')
                          if (val === '') {
                            updateLineItem(item.id, 'quantity', '')
                            return
                          }
                          const numVal = parseFloat(val) || 0
                          const maxQty = enforceLineItemInventory
                            ? item.variantOption?.availableQty
                            : null
                          if (maxQty != null && numVal > maxQty) {
                            updateLineItem(item.id, 'quantity', maxQty.toString())
                            return
                          }
                          updateLineItem(item.id, 'quantity', val)
                        }}
                        className={`${inputSmCls} w-full`}
                      />
                    </td>

                    {/* Unit price */}
                    <td className='p-3 border-b border-[#dee2e6] align-middle'>
                      <input
                        type='number'
                        step='any'
                        value={item.price}
                        onChange={(e) => updateLineItem(item.id, 'price', e.target.value === '' ? '' : e.target.value.replace(/^0+(?=\d)/, ''))}
                        className={`${inputSmCls} w-full ${(item.price === null || item.price === undefined || item.price === '' as any) && hasAttemptedSave ? 'border-danger' : ''}`}
                      />
                    </td>

                    {/* Discount */}
                    <td className='p-3 border-b border-[#dee2e6] align-middle'>
                      <input
                        type='number'
                        step='any'
                        value={item.discount}
                        onChange={(e) => updateLineItem(item.id, 'discount', e.target.value === '' ? '' : e.target.value.replace(/^0+(?=\d)/, ''))}
                        className={`${inputSmCls} w-full`}
                      />
                    </td>

                    {/* Subtotal */}
                    <td className='p-3 border-b border-[#dee2e6] align-middle text-left font-semibold'>
                      {currencySymbol}{getLineSubtotal(item).toFixed(2)}
                    </td>

                    {/* Remove */}
                    <td className='p-3 border-b border-[#dee2e6] align-middle text-center'>
                      <OmsTooltip tip='Remove this line item from the order'>
                        <button
                          type='button'
                          onClick={() => removeLineItem(item.id)}
                          className='px-[10px] py-1 rounded-[4px] text-[13px] font-medium cursor-pointer inline-flex items-center gap-[6px] bg-[#fff5f5] border border-[#f5c6cb] text-[#dc3545] hover:bg-[#f8d7da]'
                        >
                          🗑 Delete
                        </button>
                      </OmsTooltip>
                    </td>
                  </tr>
                ))
              )}
              <tr>
                <td className='p-3 border-b border-[#dee2e6] pt-4' colSpan={6}>
                  <div className='flex items-center gap-3'>
                    <OmsTooltip
                      tip={
                        warehouseId
                          ? 'Search and add another variant from the selected warehouse'
                          : 'Select a warehouse first to add line items'
                      }
                    >
                      <button
                        type='button'
                        onClick={addLineItem}
                        disabled={!warehouseId}
                        className='oms-btn oms-btn--primary'
                        style={!warehouseId ? {opacity: 0.55, cursor: 'not-allowed'} : {}}
                      >
                        + Add Line Item
                      </button>
                    </OmsTooltip>
                    {!warehouseId && (
                      <span className='text-[12px] text-[#6c757d]'>
                        Select a warehouse above first to add line items
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          {/* ── Totals block ── */}
          <div className='ml-auto mt-[20px] flex flex-col gap-[8px]' style={{ width: '440px' }}>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText text='Shipping Fee' tip='Additional shipping charge added to the order total' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <input
                  type='number'
                  step='any'
                  value={shippingFee}
                  onChange={(e) => setShippingFee(e.target.value === '' ? '' : e.target.value.replace(/^0+(?=\d)/, ''))}
                  className={`${inputSmCls} w-[120px] text-right`}
                />
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText
                text='Tax Percent (%)'
                tip='Tax percentage applied to item subtotal plus shipping'
                className='whitespace-nowrap'
              />
              <div className='w-[200px] flex justify-end items-center gap-1.5'>
                <input
                  type='number'
                  step='any'
                  min={0}
                  value={taxPercent === null ? '' : taxPercent}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === '') {
                      setTaxPercent(null)
                    } else {
                      const num = parseFloat(val)
                      setTaxPercent(isNaN(num) ? null : Math.max(0, num))
                    }
                  }}
                  placeholder='e.g. 10'
                  className={`${inputSmCls} w-[120px] text-right`}
                />
                <span className='text-[14px] text-gray-800 font-bold ms-1 select-none'>%</span>
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText
                text={`Tax Amount (${currencySymbol})`}
                tip='Calculated from tax percent applied to item subtotal plus shipping'
                className='whitespace-nowrap'
              />
              <div className='w-[200px] flex justify-end'>
                <input
                  type='text'
                  readOnly
                  disabled
                  value={calculatedTax.toFixed(2)}
                  className={`${inputSmCls} w-[120px] text-right`}
                  tabIndex={-1}
                  aria-readonly
                />
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText text='Order Discount' tip='Order-level discount applied to line subtotal plus shipping. Choose a flat amount or percentage.' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end items-center gap-2'>
                <select
                  value={orderDiscountMode}
                  onChange={(e) => setOrderDiscountMode(e.target.value as OrderDiscountMode)}
                  className={`${inputSmCls} w-[52px]`}
                >
                  <option value='amount'>{currencySymbol}</option>
                  <option value='percent'>%</option>
                </select>
                <input
                  type='number'
                  step='any'
                  min={0}
                  max={orderDiscountMode === 'percent' ? 100 : undefined}
                  value={orderDiscountValue}
                  placeholder={orderDiscountMode === 'percent' ? 'e.g. 2' : 'e.g. 100'}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/^0+(?=\d)/, '')
                    if (raw === '') {
                      setOrderDiscountValue('')
                      return
                    }
                    const numVal = parseFloat(raw) || 0
                    if (numVal < 0) {
                      setOrderDiscountValue('0')
                    } else if (orderDiscountMode === 'percent' && numVal > 100) {
                      setOrderDiscountValue('100')
                    } else {
                      setOrderDiscountValue(raw)
                    }
                  }}
                  className={`${inputSmCls} w-[120px] text-right`}
                />
              </div>
            </div>
            <div className='flex justify-between py-[4px] items-center text-[14px] font-semibold border-t border-[#dee2e6] mt-[4px] gap-[16px]'>
              <OmsInfoText text='Estimated Grand Total' tip='Grand Total including tax and Shipping fees' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <span>{currencySymbol}{grandTotal.toFixed(2)}</span>
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] text-[#28a745] font-semibold gap-[16px]'>
              <OmsInfoText text={`Amount Paid (${currencySymbol})`} tip='Total amount received for this order' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <input
                  type='number'
                  step='any'
                  min={0}
                  value={amountPaid}
                  onChange={(e) => {
                    let val = e.target.value.replace(/^0+(?=\d)/, '')
                    if (val === '') {
                      setAmountPaid('')
                      return
                    }
                    const numVal = parseFloat(val) || 0
                    if (numVal < 0) {
                      setAmountPaid('0')
                    } else if (numVal > grandTotal) {
                      setAmountPaid(grandTotal.toString())
                    } else {
                      setAmountPaid(val)
                    }
                  }}
                  className={`${inputSmCls} w-[120px] text-right`}
                  style={{borderColor: 'rgba(40, 167, 69, 0.45)'}}
                />
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText text='Payment Reference / Txn ID' tip='External payment transaction or check reference number' className='text-[12px] text-[#6c757d] whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <input
                  type='text'
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  placeholder='e.g. TXN-98421, Check #104'
                  className={`${inputSmCls} w-[170px]`}
                />
              </div>
            </div>
            <hr className='border-0 border-t border-[#dee2e6] my-1' />
            <div className='flex justify-between items-center font-bold text-[16px] text-[#007bff] gap-[16px]'>
              <OmsInfoText text='Balance Due' tip='The remaining amount to be paid' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <span>{currencySymbol}{balanceDue.toFixed(2)}</span>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* ══ Section 4 · Notes ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Order Notes &amp; Documentation</h2></div>
          <div className={sectionBodyCls}>
          <div className='grid grid-cols-2 gap-[20px]'>
            <div>
              <OmsInfoLabel text='Internal Comments (Invisible to Customer)' tip='Private operational notes. Never shown to the customer or on documents.' />
              <textarea
                className={`${inputCls} resize-y min-h-[80px]`}
                placeholder='Add confidential operational details, approval logs, backorder conditions, or picker instructions here...'
                value={internalComments}
                onChange={(e) => setInternalComments(e.target.value)}
              />
            </div>
            <div>
              <OmsInfoLabel text='Customer Notes (Visible on Invoice/Receipt)' tip='Notes shown to the customer on the invoice and receipt documents.' />
              <textarea
                className={`${inputCls} resize-y min-h-[80px]`}
                placeholder='Add custom greetings, special delivery handling instructions, or client requests to display on documents...'
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
              />
            </div>
          </div>
          </div>
        </div>

        {/* ══ Section 5 · Custom Fields ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Custom Fields</h2></div>
          <div className={sectionBodyCls}>
            <OrderCustomFields
            values={customFieldsFormik.values}
            setFormikState={customFieldsFormik.setFormikState}
            mode='create'
            onFieldsMetaLoaded={setOrderFieldsMeta}
          />
          </div>
        </div>

      </div>{/* omsOrderScope__content */}
      </div>{/* omsOrderScope__scroll */}
    </div>
  )
}

export default CreateOmsOrder
